import React, { useEffect, useState } from "react";
import api, { formatErr } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useSystem, SYSTEM_META, SERVICE_META } from "@/lib/system";
import { Panel, Badge } from "@/components/Bits";
import { Shuffle, AlertTriangle, CheckCircle2, Filter, Wand2 } from "lucide-react";

const ipt = "w-full bg-bg border border-line rounded-sm px-3 py-2 text-sm focus:border-cyan focus:outline-none focus:ring-1 focus:ring-cyan/40";

const Field = ({ label, children, hint }) => (
  <div>
    <label className="block text-[10px] uppercase tracking-[0.2em] text-slate-400 mb-1">{label}</label>
    {children}
    {hint && <div className="text-[10px] text-slate-500 mt-1">{hint}</div>}
  </div>
);

export default function BulkEdit() {
  const { user } = useAuth();
  const canEdit = ["admin", "supervisor"].includes(user?.role);
  const [merchants, setMerchants] = useState([]);
  const [preview, setPreview] = useState(null);
  const [previewBusy, setPreviewBusy] = useState(false);
  const [runBusy, setRunBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState("");

  // Filter
  const [filter, setFilter] = useState({
    system: "",
    merchant_name: "",
    status: "",
    service_type: "",
  });

  // Actions
  const [actions, setActions] = useState({
    set_system: "",
    set_customer_type: "",
    set_service_type: "",
    set_payment_channel: "",
  });

  useEffect(() => {
    api.get("/merchants").then((r) => setMerchants(r.data));
  }, []);

  const hasFilter = Object.values(filter).some((v) => v);
  const hasAction = Object.values(actions).some((v) => v);

  const buildParams = () => {
    const p = { limit: 1 };
    Object.entries(filter).forEach(([k, v]) => {
      if (!v) return;
      if (k === "merchant_name") p.merchant = v;
      else p[k] = v;
    });
    return p;
  };

  const doPreview = async () => {
    setPreviewBusy(true); setErr(""); setResult(null);
    try {
      const { headers } = await api.get("/tickets", { params: { ...buildParams(), limit: 1 } });
      // Use total via separate count call (list endpoint doesn't return total) — workaround: fetch with high limit
      const { data } = await api.get("/tickets", { params: { ...buildParams(), limit: 10000 } });
      setPreview({ count: data.length, sample: data.slice(0, 5) });
    } catch (e) { setErr(formatErr(e)); }
    finally { setPreviewBusy(false); }
  };

  const doRun = async () => {
    if (!hasFilter) { setErr("Pilih minimal 1 filter."); return; }
    if (!hasAction) { setErr("Pilih minimal 1 action."); return; }
    if (!window.confirm(`Apply changes to ${preview?.count ?? "?"} tickets? Aksi ini tidak dapat di-undo otomatis.`)) return;
    setRunBusy(true); setErr(""); setResult(null);
    try {
      const body = {};
      Object.entries(filter).forEach(([k, v]) => { if (v) body[k] = v; });
      Object.entries(actions).forEach(([k, v]) => { if (v) body[k] = v; });
      const { data } = await api.post("/tickets/bulk-update", body);
      setResult(data);
      // refresh preview
      doPreview();
    } catch (e) { setErr(formatErr(e)); }
    finally { setRunBusy(false); }
  };

  const resetAll = () => {
    setFilter({ system: "", merchant_name: "", status: "", service_type: "" });
    setActions({ set_system: "", set_customer_type: "", set_service_type: "", set_payment_channel: "" });
    setPreview(null); setResult(null); setErr("");
  };

  if (!canEdit) {
    return <div className="p-8 text-slate-400 text-sm" data-testid="bulk-edit-forbidden">Hanya admin/supervisor yang dapat menggunakan Bulk Edit.</div>;
  }

  return (
    <div className="p-6 space-y-4 max-w-5xl animate-slide-up" data-testid="bulk-edit-page">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-cyan mb-1">Operations</div>
          <h1 className="text-2xl font-bold tracking-tight">Bulk Edit Tickets</h1>
          <p className="text-xs text-slate-400 mt-1 max-w-2xl">Filter banyak tiket sekaligus, lalu apply perubahan massal. Cocok untuk fix kesalahan tagging system, ubah service/channel, atau reassign customer type.</p>
        </div>
        <button data-testid="reset-all-btn" onClick={resetAll} className="text-xs text-slate-400 border border-line px-3 py-1.5 rounded-sm hover:bg-surface2">Reset</button>
      </div>

      <Panel title="Step 1 — Filter tickets" testid="step-filter">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Field label="System">
            <select data-testid="bf-system" value={filter.system} onChange={(e) => setFilter({ ...filter, system: e.target.value })} className={ipt}>
              <option value="">Any</option>
              <option value="TARAPAY">Tarapay (TARA)</option>
              <option value="SATURNUS">Saturnuspay (STPAY)</option>
            </select>
          </Field>
          <Field label="Merchant">
            <select data-testid="bf-merchant" value={filter.merchant_name} onChange={(e) => setFilter({ ...filter, merchant_name: e.target.value })} className={ipt}>
              <option value="">Any merchant</option>
              {merchants.map((m) => <option key={m.id} value={m.name}>{m.name} · {SYSTEM_META[m.system]?.short}</option>)}
            </select>
          </Field>
          <Field label="Status">
            <select data-testid="bf-status" value={filter.status} onChange={(e) => setFilter({ ...filter, status: e.target.value })} className={ipt}>
              <option value="">Any</option>
              <option value="NEW">New</option>
              <option value="IN_PROGRESS">In Progress</option>
              <option value="ESCALATED_TO_BANK">Escalated</option>
              <option value="AWAITING_FINAL_STATUS">Awaiting</option>
              <option value="RESOLVED">Resolved</option>
              <option value="CLOSED">Closed</option>
            </select>
          </Field>
          <Field label="Service">
            <select data-testid="bf-service" value={filter.service_type} onChange={(e) => setFilter({ ...filter, service_type: e.target.value })} className={ipt}>
              <option value="">Any</option>
              <option value="PAYIN">Pay-In</option>
              <option value="PAYOUT">Payout</option>
              <option value="WITHDRAWAL">Withdrawal</option>
              <option value="TOPUP">Top-Up</option>
            </select>
          </Field>
        </div>
        <div className="mt-3 flex items-center justify-between">
          <div className="text-[10px] uppercase tracking-wider text-slate-500">
            {hasFilter ? <span className="text-cyan">Filter active</span> : "No filter — refuse to update all"}
          </div>
          <button data-testid="preview-btn" onClick={doPreview} disabled={!hasFilter || previewBusy} className="bg-surface2 border border-line text-cyan hover:bg-cyan/10 px-3 py-1.5 text-xs uppercase tracking-wider rounded-sm flex items-center gap-1.5 disabled:opacity-40">
            <Filter className="w-3 h-3" /> {previewBusy ? "Counting..." : "Preview matches"}
          </button>
        </div>
        {preview && (
          <div className="mt-3 border border-cyan/30 bg-cyan/5 rounded-sm p-3" data-testid="preview-result">
            <div className="text-xs text-cyan mb-2 font-semibold">
              {preview.count} ticket{preview.count === 1 ? "" : "s"} match.
            </div>
            <div className="space-y-1 text-[10px] font-mono text-slate-400">
              {preview.sample.map((t) => (
                <div key={t.id}>· {t.ticket_number} · {t.system} · {t.service_type} · {t.merchant_name} · {t.status}</div>
              ))}
              {preview.count > preview.sample.length && <div>... and {preview.count - preview.sample.length} more</div>}
            </div>
          </div>
        )}
      </Panel>

      <Panel title="Step 2 — Choose actions" testid="step-actions">
        <p className="text-[10px] text-slate-500 mb-3 uppercase tracking-wider">Kosongkan field yang tidak ingin Anda ubah. Minimal 1 wajib diisi.</p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Field label="Move to System">
            <select data-testid="ba-system" value={actions.set_system} onChange={(e) => setActions({ ...actions, set_system: e.target.value })} className={ipt}>
              <option value="">— no change —</option>
              <option value="TARAPAY">Tarapay</option>
              <option value="SATURNUS">Saturnuspay</option>
            </select>
          </Field>
          <Field label="Set Customer Type">
            <select data-testid="ba-customer-type" value={actions.set_customer_type} onChange={(e) => setActions({ ...actions, set_customer_type: e.target.value })} className={ipt}>
              <option value="">— no change —</option>
              <option value="FOREX">Forex</option>
              <option value="GAMING">Gaming</option>
            </select>
          </Field>
          <Field label="Set Service Type">
            <select data-testid="ba-service-type" value={actions.set_service_type} onChange={(e) => setActions({ ...actions, set_service_type: e.target.value })} className={ipt}>
              <option value="">— no change —</option>
              <option value="PAYIN">Pay-In</option>
              <option value="PAYOUT">Payout</option>
              <option value="WITHDRAWAL">Withdrawal</option>
              <option value="TOPUP">Top-Up</option>
            </select>
          </Field>
          <Field label="Set Payment Channel">
            <select data-testid="ba-payment-channel" value={actions.set_payment_channel} onChange={(e) => setActions({ ...actions, set_payment_channel: e.target.value })} className={ipt}>
              <option value="">— no change —</option>
              <option value="Ayolinx">Ayolinx</option>
              <option value="Pakailink">Pakailink</option>
              <option value="Pearpaygame">Pearpaygame</option>
              <option value="Easylink">Easylink</option>
            </select>
          </Field>
        </div>
      </Panel>

      {err && <div className="px-3 py-2 border border-rose-500/30 bg-rose-500/10 text-rose-400 text-xs rounded-sm" data-testid="bulk-edit-err">{err}</div>}

      <div className="flex items-center justify-between bg-surface border border-line rounded-sm p-4">
        <div className="flex items-start gap-2 text-xs text-amber-400">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
          <div>
            <div className="font-semibold">Aksi tidak dapat di-undo otomatis.</div>
            <div className="text-slate-400">Jalankan Preview dulu sebelum apply. Perubahan akan tercatat di log audit per-tiket (Timeline).</div>
          </div>
        </div>
        <button
          data-testid="apply-btn"
          onClick={doRun}
          disabled={!hasFilter || !hasAction || runBusy}
          className="bg-amber-500 text-bg font-semibold px-5 py-2.5 rounded-sm text-sm flex items-center gap-2 hover:bg-amber-400 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <Wand2 className="w-4 h-4" /> {runBusy ? "Applying..." : "Apply Bulk Edit"}
        </button>
      </div>

      {result && (
        <Panel title="Result" testid="result-panel">
          <div className="flex items-start gap-3">
            <CheckCircle2 className="w-5 h-5 text-emerald-400 mt-0.5" />
            <div>
              <div className="text-sm font-semibold text-emerald-400" data-testid="result-modified">
                ✓ {result.modified} of {result.matched} tickets modified.
              </div>
              <div className="text-[10px] text-slate-500 font-mono mt-2 space-y-0.5">
                <div>Filter: {JSON.stringify(result.filter)}</div>
                <div>Updates: {JSON.stringify(result.updates)}</div>
              </div>
            </div>
          </div>
        </Panel>
      )}
    </div>
  );
}
